import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Music2, Users, Disc3, CalendarDays, MapPin, BarChart3, ExternalLink, Heart, Mail, Globe, Video, MessageCircle } from "lucide-react"

type Stats = {
  bands: number
  people: number
  recordings: number
  venues: number
  gigs: number
  chart_listings: number
}

type Links = { blog: string; youtube: string; facebook: string; x: string }

type Props = { stats: Stats; links: Links }

function StatItem({ icon: Icon, value, label }: { icon: React.ElementType; value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 py-5 px-4">
      <Icon className="w-5 h-5 text-amber-500 mb-1" />
      <span className="text-2xl font-bold text-stone-900 dark:text-white tabular-nums">{value.toLocaleString()}</span>
      <span className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide font-medium">{label}</span>
    </div>
  )
}

const title       = "About — Australian Music Database"
const description = "The Australian Music Database is a living archive connecting Australian bands, musicians, recordings, gigs, and venues across the decades."

export default function About({ stats, links }: Props) {
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicShell>
        <div className="max-w-3xl mx-auto space-y-12 py-4">

          {/* Hero */}
          <section>
            <h1 className="mb-4">About this site</h1>
            <p className="text-base text-stone-600 dark:text-stone-300 leading-relaxed mb-4">
              The Australian Music Database is a living archive of Australian bands, musicians, recordings, gigs, and venues — with a particular focus on the pub rock era and the decades of rock and pop that followed. It has been running continuously since 2007.
            </p>
            <p className="text-base text-stone-600 dark:text-stone-300 leading-relaxed">
              The database isn't a static encyclopaedia. It grows and changes as new research is added, mistakes are corrected, and connections between artists are traced. The goal is to document the full web of relationships that makes Australian music history so rich — who played with whom, what they recorded, where they played, and how it all connects.
            </p>
          </section>

          {/* Stats */}
          <section className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-2 border-b border-stone-100 dark:border-stone-800">
              <h2 className="text-base font-semibold text-stone-900 dark:text-white">The database, by the numbers</h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-stone-100 dark:divide-stone-800">
              <StatItem icon={Music2}       value={stats.bands}          label="Bands" />
              <StatItem icon={Users}        value={stats.people}         label="People" />
              <StatItem icon={Disc3}        value={stats.recordings}     label="Recordings" />
              <StatItem icon={CalendarDays} value={stats.gigs}           label="Gigs" />
              <StatItem icon={MapPin}       value={stats.venues}         label="Venues" />
              <StatItem icon={BarChart3}    value={stats.chart_listings} label="Chart entries" />
            </div>
          </section>

          {/* What it does */}
          <section>
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-4">What it does</h2>
            <div className="space-y-4 text-base text-stone-600 dark:text-stone-300 leading-relaxed">
              <p>
                At its core, the database is a <strong className="text-stone-800 dark:text-stone-200">relationship engine</strong>. Every band, person, recording, and venue is linked to every other through a web of memberships, credits, and gigs. You can trace a chain like{" "}
                <Link href="/people/john-cave-aka-william-shakespeare" className="text-amber-600 dark:text-amber-400 hover:underline">William Shakespeare</Link>
                {" → "}
                <Link href="/people/harry-brus" className="text-amber-600 dark:text-amber-400 hover:underline">Harry Brus</Link>
                {" → "}
                <Link href="/bands/australian-crawl" className="text-amber-600 dark:text-amber-400 hover:underline">Australian Crawl</Link>
                {" → "}
                <Link href="/people/david-briggs" className="text-amber-600 dark:text-amber-400 hover:underline">David Briggs</Link>
                {" → "}
                <Link href="/bands/little-river-band" className="text-amber-600 dark:text-amber-400 hover:underline">Little River Band</Link>
                {" "} — and keep going.
              </p>
              <p>
                Recording pages list chart performance — peak position, weeks in chart, and for many records, the full week-by-week chart history. Venue pages collect every gig logged at that venue. Person pages show band memberships across a career, with credits for production, session, and behind-the-scenes work.
              </p>
              <p>
                The database covers all genres, though its depth is greatest in Australian rock and pop from the 1960s onward. It's open to any Australian artist, band, or venue — there's no genre gate.
              </p>
            </div>
          </section>

          {/* Sister sites */}
          <section>
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-4">Part of a wider ecosystem</h2>
            <p className="text-base text-stone-600 dark:text-stone-300 leading-relaxed mb-5">
              The Australian Music Database is one part of a broader project documenting Australian music history. The research done here feeds into:
            </p>
            <ul className="space-y-3">
              {[
                {
                  icon: ExternalLink,
                  label: "Australian Music History",
                  href: links.blog,
                  description: "The sister blog — long-form articles, timelines, and features on Australian artists and eras.",
                  external: true,
                },
                {
                  icon: Video,
                  label: "YouTube channel",
                  href: links.youtube,
                  description: "Video essays and archival footage on Australian music.",
                  external: true,
                },
                {
                  icon: Globe,
                  label: "Facebook page",
                  href: links.facebook,
                  description: "Daily \"on this day\" posts, birthday spotlights, and chart anniversaries.",
                  external: true,
                },
                {
                  // NB: the X handle has no "17" — that suffix belongs to the
                  // YouTube handle above. See #358.
                  icon: MessageCircle,
                  label: "X",
                  href: links.x,
                  description: "The same daily roundup, posted to X.",
                  external: true,
                },
              ].map(({ icon: Icon, label, href, description, external }) => (
                <li key={href}>
                  <a
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-3 p-4 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl hover:border-amber-300 dark:hover:border-amber-700 transition-colors no-underline group"
                  >
                    <Icon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors block">{label}</span>
                      <span className="text-xs text-stone-500 dark:text-stone-400 mt-0.5 block">{description}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {/* Contributing */}
          <section>
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-4">Contribute or correct</h2>
            <div className="space-y-4 text-base text-stone-600 dark:text-stone-300 leading-relaxed">
              <p>
                The database is maintained by Mark Gibson. If you spot a mistake, know of a missing artist, or have gig history or chart data to contribute, please get in touch — via the Facebook page or by email. Everything helps, and all contributions are credited.
              </p>
              <p>
                The best way to reach us is through the{" "}
                <a href={links.facebook} target="_blank" rel="noopener noreferrer" className="text-amber-600 dark:text-amber-400 hover:underline">Facebook page</a>
                {" "}or the contact link below.
              </p>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-4">Common questions</h2>
            <div className="space-y-6">
              {[
                {
                  q: "I found a mistake. How do I report it?",
                  a: "Use the contact link below, or — if you have an account — edit the record directly. All changes are logged.",
                },
                {
                  q: "How do I get my band listed?",
                  a: "Send a message with the band name, members, and any recordings or gig history you have. We'll add it in.",
                },
                {
                  q: "What genres does the database cover?",
                  a: "All genres, all eras. The coverage is deepest in Australian rock and pop from the 1960s onward, but there's no genre restriction — if you're Australian and made music, you belong here.",
                },
                {
                  q: "Is there an API?",
                  a: "Not yet. If you're a researcher or developer with a specific use case, get in touch.",
                },
              ].map(({ q, a }) => (
                <div key={q} className="border-b border-stone-100 dark:border-stone-800 pb-6 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-stone-900 dark:text-white mb-2">{q}</h3>
                  <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact + Support */}
          <section className="grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:australianmusichistory@gmail.com"
              className="flex items-center gap-3 p-5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl hover:border-amber-300 dark:hover:border-amber-700 transition-colors no-underline group"
            >
              <Mail className="w-5 h-5 text-amber-500 shrink-0" />
              <div>
                <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors block">Get in touch</span>
                <span className="text-xs text-stone-500 dark:text-stone-400 block mt-0.5">Corrections, additions, questions</span>
              </div>
            </a>
            <Link
              href="/donate"
              className="flex items-center gap-3 p-5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl hover:border-amber-400 dark:hover:border-amber-700 transition-colors no-underline group"
            >
              <Heart className="w-5 h-5 text-amber-500 shrink-0" />
              <div>
                <span className="text-sm font-medium text-amber-800 dark:text-amber-300 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors block">Support the project</span>
                <span className="text-xs text-amber-600/70 dark:text-amber-500/70 block mt-0.5">Help keep it running and growing</span>
              </div>
            </Link>
          </section>

          <p className="text-xs text-stone-400 dark:text-stone-600 text-center pb-4">
            © 2007–{new Date().getFullYear()} Australian Music Database. Running since 2007.
          </p>

        </div>
      </PublicShell>
    </>
  )
}
